/**
 * Cloudflare Platform - Wizard Connect + Explorer
 */
import axios from 'axios'
import {
    AlertCircle,
    ArrowRight,
    Check,
    ChevronDown, ChevronRight,
    Cloud, Database, ExternalLink,
    Globe, HardDrive, Key, Loader2,
    Lock,
    RefreshCw,
    Shield,
    Trash2,
    Zap
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface CFAccount {
  id: number
  account_id: string
  account_name: string
  workers_count: number
  d1_count: number
  r2_count: number
  kv_count: number
  status: string
  last_synced_at: string | null
}

interface CFRow { id: string; name: string; meta: string }
interface AIModel {
  id: string; name: string; description: string; task: string; provider: string
  created_at: string; context_window: number; function_calling: boolean
  vision: boolean; reasoning: boolean; batch: boolean; lora: boolean; beta: boolean
  price_input: string; price_output: string; tags: string[]; source: number
}
type ExplorerTab = 'workers' | 'd1' | 'r2' | 'kv' | 'vectorize' | 'analytics'
type WizardStep = 'idle' | 'credentials' | 'connecting' | 'setup' | 'creating' | 'done'

interface SetupForm {
  createD1: boolean; d1Name: string
  createR2: boolean; r2Name: string
  createKv: boolean; kvName: string
  createGateway: boolean; gatewayName: string
}

interface ConnectedAccount { id: number; name: string }

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` })

// ─── Resource Card ────────────────────────────────────────────────────────────

function ResourceCard({ icon: Icon, label, count, desc, color, active, onClick }: {
  icon: React.ElementType; label: string; count: number; desc: string
  color: string; active: boolean; onClick: () => void
}) {
  return (
    <div onClick={onClick} style={{
      padding: '16px', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s',
      background: active ? `${color}15` : 'var(--body-bg)',
      border: `1.5px solid ${active ? color : 'var(--border-color)'}`,
      display: 'flex', flexDirection: 'column', gap: '6px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={color} />
        </div>
        <span style={{ fontSize: '22px', fontWeight: 700, color: active ? color : 'var(--text-primary)' }}>{count}</span>
      </div>
      <div style={{ fontSize: '13px', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{desc}</div>
    </div>
  )
}

// ─── Explorer ─────────────────────────────────────────────────────────────────

function Explorer({ account, tab }: { account: CFAccount; tab: ExplorerTab }) {
  const [rows, setRows]         = useState<CFRow[]>([])
  const [models, setModels]     = useState<AIModel[]>([])
  const [loading, setLoading]   = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [detail, setDetail]     = useState<Record<string, unknown> | null>(null)
  const [search, setSearch]     = useState('')
  const [taskFilter, setTaskFilter] = useState('')
  const [providerFilter, setProviderFilter] = useState('')
  const [capFilter, setCapFilter] = useState('')

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const r = await axios.get(`${API_URL}/api/aicloud/cloudflare/accounts/${account.id}/explore/${tab}`, { headers: authHeader() })
        const d = r.data as { items?: unknown[]; count?: number }
        setExpanded(null); setDetail(null)
        const items = d.items as Record<string, unknown>[] ?? []
        if (tab === 'workers') {
          // API'den task obje gelebilir, string'e cevir
          const parsed = items.map((raw, idx) => {
            const r = raw
            const taskVal = r.task
            const taskStr = typeof taskVal === 'object' && taskVal !== null
              ? String((taskVal as Record<string, string>).name ?? '')
              : String((taskVal as string) ?? '')
            const tagsVal = r.tags
            const tagsArr = Array.isArray(tagsVal)
              ? tagsVal.map(t => typeof t === 'string' ? t : String((t as Record<string, string>).name ?? ''))
              : []
            return {
              id: String((r.id as string) ?? String(idx)),
              name: String((r.name as string) ?? ''),
              description: String((r.description as string) ?? ''),
              task: taskStr,
              task_id: String((r.task_id as string) ?? ''),
              provider: String((r.provider as string) ?? ''),
              created_at: String((r.created_at as string) ?? ''),
              context_window: Number(r.context_window ?? 0),
              function_calling: Boolean(r.function_calling),
              vision: Boolean(r.vision),
              reasoning: Boolean(r.reasoning),
              batch: Boolean(r.batch),
              lora: Boolean(r.lora),
              beta: Boolean(r.beta),
              price_input: String((r.price_input as string) ?? ''),
              price_output: String((r.price_output as string) ?? ''),
              tags: tagsArr,
              source: Number(r.source ?? 0),
            } as AIModel
          })
          setModels(parsed)
          setRows([])
        } else {
          setModels([])
          if (tab === 'd1')  { setRows(items.map(x => ({ id: String((x.uuid as string) ?? (x.id as string) ?? ''), name: String((x.name as string) ?? ''), meta: x.num_tables !== undefined && x.num_tables !== null ? `${String(x.num_tables as number)} tables` : '' }))) }
          if (tab === 'r2')  { setRows(items.map(x => ({ id: String((x.name as string) ?? ''), name: String((x.name as string) ?? ''), meta: x.creation_date ? new Date(String(x.creation_date as string)).toLocaleDateString() : '' }))) }
          if (tab === 'kv')  { setRows(items.map(x => ({ id: String((x.id as string) ?? ''), name: String((x.title as string) ?? (x.id as string) ?? ''), meta: String((x.id as string) ?? '').slice(0, 8) + '...' }))) }
        }
      } catch { setRows([]); setModels([]) }
      finally { setLoading(false) }
    }
    void fetchData()
  }, [account.id, tab])  

  const toggleDetail = async (id: string) => {
    if (expanded === id) { setExpanded(null); setDetail(null); return }
    setExpanded(id); setDetail(null)
    if (tab !== 'workers') {
      try {
        const r = await axios.get(`${API_URL}/api/aicloud/cloudflare/accounts/${account.id}/explore/${tab}/${encodeURIComponent(id)}`, { headers: authHeader() })
        setDetail(r.data as Record<string, unknown>)
      } catch { setDetail(null) }
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
      </div>
    )
  }

  // Workers AI — zengin model kartlari
  if (tab === 'workers' && models.length > 0) {
    const tasks = [...new Set(models.map(m => m.task).filter(Boolean))].sort()
    const providers = [...new Set(models.map(m => m.provider).filter(Boolean))].sort()
    const filtered = models.filter(m => {
      if (search && !m.name.toLowerCase().includes(search.toLowerCase()) && !m.description.toLowerCase().includes(search.toLowerCase()) && !m.provider.toLowerCase().includes(search.toLowerCase())) { return false }
      if (taskFilter && m.task !== taskFilter) { return false }
      if (providerFilter && m.provider !== providerFilter) { return false }
      if (capFilter === 'function_calling' && !m.function_calling) { return false }
      if (capFilter === 'vision' && !m.vision) { return false }
      if (capFilter === 'reasoning' && !m.reasoning) { return false }
      if (capFilter === 'batch' && !m.batch) { return false }
      if (capFilter === 'lora' && !m.lora) { return false }
      return true
    })

    const taskColors: Record<string, string> = {
      'Text Generation': '#6366f1', 'Text-to-Image': '#ec4899', 'Text-to-Speech': '#f59e0b',
      'Automatic Speech Recognition': '#10b981', 'Text Embeddings': '#8b5cf6',
      'Translation': '#06b6d4', 'Image Classification': '#f97316', 'Object Detection': '#ef4444',
      'Summarization': '#14b8a6', 'Image-to-Text': '#a855f7',
    }

    return (
      <div style={{ padding: '16px 20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>Workers AI Models <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>({models.length} available)</span></div>
          <span style={{ fontSize: '12px', color: filtered.length < models.length ? '#f59e0b' : 'var(--text-secondary)' }}>{filtered.length} shown</span>
        </div>

        {/* Search */}
        <input placeholder="Search models, providers..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', padding: '9px 12px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', marginBottom: '10px', boxSizing: 'border-box' }} />

        {/* Filter row */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <select value={taskFilter} onChange={e => setTaskFilter(e.target.value)}
            style={{ padding: '6px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '11px', outline: 'none' }}>
            <option value="">All Tasks</option>
            {tasks.map(t => <option key={t} value={t}>{t} ({models.filter(m => m.task === t).length})</option>)}
          </select>
          <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)}
            style={{ padding: '6px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '11px', outline: 'none' }}>
            <option value="">All Providers</option>
            {providers.map(p => <option key={p} value={p}>{p} ({models.filter(m => m.provider === p).length})</option>)}
          </select>
          <select value={capFilter} onChange={e => setCapFilter(e.target.value)}
            style={{ padding: '6px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '11px', outline: 'none' }}>
            <option value="">All Capabilities</option>
            <option value="function_calling">Function Calling ({models.filter(m => m.function_calling).length})</option>
            <option value="vision">Vision ({models.filter(m => m.vision).length})</option>
            <option value="reasoning">Reasoning ({models.filter(m => m.reasoning).length})</option>
            <option value="batch">Batch ({models.filter(m => m.batch).length})</option>
            <option value="lora">LoRA ({models.filter(m => m.lora).length})</option>
          </select>
          {(taskFilter || providerFilter || capFilter || search) && (
            <button onClick={() => { setTaskFilter(''); setProviderFilter(''); setCapFilter(''); setSearch('') }}
              style={{ padding: '6px 10px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '6px', color: '#ef4444', fontSize: '11px', cursor: 'pointer' }}>
              Clear filters
            </button>
          )}
        </div>

        {/* Model Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px', maxHeight: '600px', overflowY: 'auto' }}>
          {filtered.map(m => {
            const tc = taskColors[m.task] || '#6b7280'
            return (
              <div key={m.id} style={{ padding: '14px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Header: name + provider */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, wordBreak: 'break-all' }}>{m.name}</div>
                    {m.provider && <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{m.provider}</div>}
                  </div>
                  {m.beta && <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 600, whiteSpace: 'nowrap' }}>BETA</span>}
                </div>

                {/* Task badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '12px', background: `${tc}15`, color: tc, fontWeight: 600 }}>{m.task || 'Unknown'}</span>
                  {m.created_at && <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>{m.created_at}</span>}
                </div>

                {/* Description */}
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {m.description || 'No description'}
                </div>

                {/* Capabilities */}
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {m.context_window > 0 && (
                    <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>
                      {m.context_window >= 1000 ? `${(m.context_window / 1000).toFixed(0)}k ctx` : `${m.context_window} ctx`}
                    </span>
                  )}
                  {m.function_calling && <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>Functions</span>}
                  {m.vision && <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(168,85,247,0.1)', color: '#a855f7' }}>Vision</span>}
                  {m.reasoning && <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(236,72,153,0.1)', color: '#ec4899' }}>Reasoning</span>}
                  {m.batch && <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(107,114,128,0.1)', color: '#6b7280' }}>Batch</span>}
                  {m.lora && <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>LoRA</span>}
                </div>

                {/* Price */}
                {(m.price_input || m.price_output) && (
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', display: 'flex', gap: '8px' }}>
                    {m.price_input && <span>In: {m.price_input}/M tok</span>}
                    {m.price_output && <span>Out: {m.price_output}/M tok</span>}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // D1 / R2 / KV — mevcut liste gorunumu
  if (rows.length === 0) {
    return <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>No resources found.</div>
  }

  return (
    <div>
      {rows.map((row, i) => (
        <div key={row.id} style={{ borderBottom: i < rows.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
          <div onClick={() => void toggleDetail(row.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 16px', cursor: 'pointer', background: expanded === row.id ? 'rgba(99,102,241,0.04)' : 'transparent' }}>
            {expanded === row.id
              ? <ChevronDown size={13} color="var(--text-secondary)" />
              : <ChevronRight size={13} color="var(--text-secondary)" />}
            <span style={{ fontSize: '13px', fontWeight: 500, flex: 1 }}>{row.name}</span>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{row.meta}</span>
            <a href={`https://dash.cloudflare.com`} target="_blank" rel="noreferrer"
              onClick={e => e.stopPropagation()}
              style={{ color: 'var(--text-secondary)', display: 'flex', padding: '2px' }}>
              <ExternalLink size={11} />
            </a>
          </div>
          {expanded === row.id && detail && (
            <div style={{ padding: '10px 16px 10px 38px', background: 'var(--card-bg)', borderTop: '1px solid var(--border-color)' }}>
              <pre style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '180px', overflowY: 'auto' }}>
                {JSON.stringify(detail, null, 2)}
              </pre>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CloudflarePage() {
  const [accounts, setAccounts]       = useState<CFAccount[]>([])
  const [loading, setLoading]         = useState(true)
  const [error, setError]             = useState<string | null>(null)
  const [wizard, setWizard]           = useState<WizardStep>('idle')
  const [email, setEmail]             = useState('')
  const [apiKey, setApiKey]           = useState('')
  const [syncing, setSyncing]         = useState<number | null>(null)
  const [activeTab, setActiveTab]     = useState<Record<number, ExplorerTab>>({})
  const [connectedAcc, setConnectedAcc] = useState<ConnectedAccount | null>(null)
  const [setupForm, setSetupForm]     = useState<SetupForm>({
    createD1: true, d1Name: 'runai-db',
    createR2: true, r2Name: 'runai-storage',
    createKv: true, kvName: 'runai-cache',
    createGateway: true, gatewayName: 'runai-gateway',
  })
  const [setupResult, setSetupResult] = useState<{ created: {type:string;name:string}[]; errors: {type:string;error:string}[] } | null>(null)

  // runai- prefix'i koru
  const withPrefix = (val: string) => val.startsWith('runai-') ? val : `runai-${val.replace(/^runai-?/, '')}`

  const fetchAccounts = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/aicloud/cloudflare/accounts`, { headers: authHeader() })
      setAccounts((r.data as { accounts: CFAccount[] }).accounts ?? [])
    } catch { setAccounts([]) }
    finally { setLoading(false) }
  }

  useEffect(() => { void fetchAccounts() }, [])

  const handleConnect = async () => {
    if (!email || !apiKey) { setError('Email and API Key required'); return }
    setWizard('connecting'); setError(null)
    try {
      const r = await axios.post(`${API_URL}/api/aicloud/cloudflare/fetch-accounts`,
        { auth_email: email, api_key: apiKey }, { headers: authHeader() })
      const cfAccounts = (r.data as { accounts: { id: string; name: string }[] }).accounts
      if (!cfAccounts?.length) { setError('No accounts found'); setWizard('credentials'); return }
      const res = await axios.post(`${API_URL}/api/aicloud/cloudflare/connect`,
        { api_token: apiKey, account_id: cfAccounts[0].id, account_name: cfAccounts[0].name, auth_email: email, use_global_key: true },
        { headers: authHeader() }
      )
      const newId = (res.data as { id?: number }).id ?? 0
      setConnectedAcc({ id: newId, name: cfAccounts[0].name })
      await fetchAccounts()
      setWizard('setup')
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Connection failed')
      setWizard('credentials')
    }
  }

  const handleSetup = async () => {
    if (!connectedAcc) {return}
    setWizard('creating')
    try {
      const r = await axios.post(
        `${API_URL}/api/aicloud/cloudflare/accounts/${connectedAcc.id}/setup`,
        {
          create_d1: setupForm.createD1, d1_name: setupForm.d1Name,
          create_r2: setupForm.createR2, r2_name: setupForm.r2Name,
          create_kv: setupForm.createKv, kv_name: setupForm.kvName,
          create_gateway: setupForm.createGateway, gateway_name: setupForm.gatewayName,
        },
        { headers: authHeader() }
      )
      setSetupResult((r.data as { results: typeof setupResult }).results)
      await fetchAccounts()
      setWizard('done')
      setTimeout(() => { setWizard('idle'); setEmail(''); setApiKey(''); setSetupResult(null) }, 3000)
    } catch {
      setWizard('done')
      setTimeout(() => { setWizard('idle'); setEmail(''); setApiKey('') }, 2000)
    }
  }

  const handleSync = async (id: number) => {
    setSyncing(id)
    try {
      await axios.post(`${API_URL}/api/aicloud/cloudflare/accounts/${id}/sync`, {}, { headers: authHeader() })
      void fetchAccounts()
    } catch { setError('Sync failed') }
    finally { setSyncing(null) }
  }

  const handleDisconnect = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/api/aicloud/cloudflare/accounts/${id}`, { headers: authHeader() })
      setAccounts(prev => prev.filter(a => a.id !== id))
    } catch { setError('Failed to disconnect') }
  }

  const getTab = (id: number): ExplorerTab => activeTab[id] ?? 'workers'
  const setTab = (id: number, tab: ExplorerTab) => setActiveTab(prev => ({ ...prev, [id]: tab }))

  const TABS: { key: ExplorerTab; label: string; icon: React.ElementType; color: string; desc: string; countKey: keyof CFAccount }[] = [
    { key: 'workers',   label: 'Workers AI',  icon: Zap,       color: '#f6821f', desc: 'AI models at edge',        countKey: 'workers_count' },
    { key: 'd1',        label: 'D1 Database',  icon: Database,  color: '#6366f1', desc: 'SQL database at edge',     countKey: 'd1_count' },
    { key: 'r2',        label: 'R2 Storage',   icon: HardDrive, color: '#10b981', desc: 'S3-compatible storage',    countKey: 'r2_count' },
    { key: 'kv',        label: 'KV Store',     icon: Key,       color: '#f59e0b', desc: 'Key-value at edge',        countKey: 'kv_count' },
    { key: 'vectorize', label: 'Vectorize',    icon: Database,  color: '#8b5cf6', desc: 'Vector DB for AI memory',  countKey: 'kv_count' },
    { key: 'analytics', label: 'Analytics',    icon: Zap,       color: '#06b6d4', desc: 'AI Gateway metrics',       countKey: 'workers_count' },
  ]

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'rgba(246,130,31,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Cloud size={22} color="#f6821f" />
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>Cloudflare</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Workers AI · D1 · R2 · KV — global edge network</div>
          </div>
        </div>
        {accounts.length > 0 && wizard === 'idle' && (
          <button onClick={() => setWizard('credentials')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#f6821f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
            + Add Account
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '18px', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* Wizard */}
      {(wizard !== 'idle' || (accounts.length === 0 && !loading)) && (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '14px', overflow: 'hidden', marginBottom: '24px' }}>

          {/* Wizard header */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)', background: 'linear-gradient(135deg, rgba(246,130,31,0.06) 0%, transparent 100%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              {[
                { step: 1, label: 'API Key',    done: ['connecting','setup','creating','done'].includes(wizard) || wizard === 'credentials' },
                { step: 2, label: 'Connect',    done: ['setup','creating','done'].includes(wizard) },
                { step: 3, label: 'Setup',      done: ['creating','done'].includes(wizard) },
                { step: 4, label: 'Ready',      done: wizard === 'done' },
              ].map((s, i) => (
                <div key={s.step} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700,
                    background: s.done ? '#f6821f' : 'var(--body-bg)',
                    color: s.done ? 'white' : 'var(--text-secondary)',
                    border: `2px solid ${s.done ? '#f6821f' : 'var(--border-color)'}`
                  }}>
                    {s.done ? <Check size={13} /> : s.step}
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: s.done ? 600 : 400, color: s.done ? 'var(--text-primary)' : 'var(--text-secondary)' }}>{s.label}</span>
                  {i < 3 && <ArrowRight size={14} color="var(--border-color)" />}
                </div>
              ))}
            </div>
          </div>

          {/* Wizard body */}
          <div style={{ padding: '24px' }}>
            {wizard === 'done' ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                  <Check size={28} color="#10b981" />
                </div>
                <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '8px' }}>All set!</div>
                {setupResult && setupResult.created.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxWidth: '280px', margin: '0 auto' }}>
                    {setupResult.created.map((c, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#10b981' }}>
                        <Check size={12} /> <span style={{ fontWeight: 600 }}>{c.type.toUpperCase()}</span> {c.name} created
                      </div>
                    ))}
                    {setupResult.errors.map((e, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#ef4444' }}>
                        <AlertCircle size={12} /> {e.type.toUpperCase()} failed
                      </div>
                    ))}
                  </div>
                )}
              </div>

            ) : wizard === 'creating' ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Loader2 size={32} color="#f6821f" style={{ animation: 'spin 1s linear infinite', margin: '0 auto 12px', display: 'block' }} />
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Creating resources...</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Setting up your Cloudflare infrastructure</div>
              </div>

            ) : wizard === 'setup' ? (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '4px' }}>
                    Connected to {connectedAcc?.name}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Choose which resources to create. Names are prefixed with <code style={{ background: 'var(--body-bg)', padding: '1px 5px', borderRadius: '4px', fontSize: '11px' }}>runai-</code> so RunAI can identify them.
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                  {/* D1 */}
                  <div style={{ padding: '14px', borderRadius: '10px', border: `1.5px solid ${setupForm.createD1 ? '#6366f1' : 'var(--border-color)'}`, background: setupForm.createD1 ? 'rgba(99,102,241,0.06)' : 'var(--body-bg)', cursor: 'pointer' }}
                    onClick={() => setSetupForm(f => ({ ...f, createD1: !f.createD1 }))}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Database size={16} color="#6366f1" />
                      <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: `2px solid ${setupForm.createD1 ? '#6366f1' : 'var(--border-color)'}`, background: setupForm.createD1 ? '#6366f1' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {setupForm.createD1 && <Check size={10} color="white" />}
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>D1 Database</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px' }}>Agent memory & project data</div>
                    {setupForm.createD1 && (
                      <input value={setupForm.d1Name} onClick={e => e.stopPropagation()}
                        onChange={e => setSetupForm(f => ({ ...f, d1Name: withPrefix(e.target.value) }))}
                        style={{ width: '100%', padding: '6px 8px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box', outline: 'none', fontFamily: 'monospace' }} />
                    )}
                  </div>

                  {/* R2 */}
                  <div style={{ padding: '14px', borderRadius: '10px', border: `1.5px solid ${setupForm.createR2 ? '#10b981' : 'var(--border-color)'}`, background: setupForm.createR2 ? 'rgba(16,185,129,0.06)' : 'var(--body-bg)', cursor: 'pointer' }}
                    onClick={() => setSetupForm(f => ({ ...f, createR2: !f.createR2 }))}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <HardDrive size={16} color="#10b981" />
                      <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: `2px solid ${setupForm.createR2 ? '#10b981' : 'var(--border-color)'}`, background: setupForm.createR2 ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {setupForm.createR2 && <Check size={10} color="white" />}
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>R2 Storage</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px' }}>Agent files, reports, outputs</div>
                    {setupForm.createR2 && (
                      <input value={setupForm.r2Name} onClick={e => e.stopPropagation()}
                        onChange={e => setSetupForm(f => ({ ...f, r2Name: withPrefix(e.target.value) }))}
                        style={{ width: '100%', padding: '6px 8px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box', outline: 'none', fontFamily: 'monospace' }} />
                    )}
                  </div>

                  {/* KV */}
                  <div style={{ padding: '14px', borderRadius: '10px', border: `1.5px solid ${setupForm.createKv ? '#f59e0b' : 'var(--border-color)'}`, background: setupForm.createKv ? 'rgba(245,158,11,0.06)' : 'var(--body-bg)', cursor: 'pointer' }}
                    onClick={() => setSetupForm(f => ({ ...f, createKv: !f.createKv }))}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <Key size={16} color="#f59e0b" />
                      <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: `2px solid ${setupForm.createKv ? '#f59e0b' : 'var(--border-color)'}`, background: setupForm.createKv ? '#f59e0b' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {setupForm.createKv && <Check size={10} color="white" />}
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '2px' }}>KV Store</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px' }}>Fast cache, sessions, config</div>
                    {setupForm.createKv && (
                      <input value={setupForm.kvName} onClick={e => e.stopPropagation()}
                        onChange={e => setSetupForm(f => ({ ...f, kvName: withPrefix(e.target.value) }))}
                        style={{ width: '100%', padding: '6px 8px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box', outline: 'none', fontFamily: 'monospace' }} />
                    )}
                  </div>
                </div>

                {/* AI Gateway */}
                <div style={{ padding: '14px', borderRadius: '10px', border: `1.5px solid ${setupForm.createGateway ? '#f6821f' : 'var(--border-color)'}`, background: setupForm.createGateway ? 'rgba(246,130,31,0.06)' : 'var(--body-bg)', cursor: 'pointer', marginBottom: '16px' }}
                  onClick={() => setSetupForm(f => ({ ...f, createGateway: !f.createGateway }))}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Zap size={16} color="#f6821f" />
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>AI Gateway</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Observe, control &amp; cache all AI requests. Your API keys stay in Cloudflare.</div>
                      </div>
                    </div>
                    <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: `2px solid ${setupForm.createGateway ? '#f6821f' : 'var(--border-color)'}`, background: setupForm.createGateway ? '#f6821f' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {setupForm.createGateway && <Check size={10} color="white" />}
                    </div>
                  </div>
                  {setupForm.createGateway && (
                    <div onClick={e => e.stopPropagation()}>
                      <input value={setupForm.gatewayName}
                        onChange={e => setSetupForm(f => ({ ...f, gatewayName: withPrefix(e.target.value) }))}
                        style={{ width: '100%', padding: '6px 8px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '12px', boxSizing: 'border-box', outline: 'none', fontFamily: 'monospace', marginBottom: '6px' }} />
                      <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                        Gateway URL will be: https://gateway.ai.cloudflare.com/v1/{connectedAcc?.name}/
                        {setupForm.gatewayName}
                      </div>
                    </div>
                  )}
                </div>

                {/* Workers AI info */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'rgba(246,130,31,0.06)', border: '1px solid rgba(246,130,31,0.2)', borderRadius: '8px', marginBottom: '16px' }}>
                  <Zap size={14} color="#f6821f" />
                  <div style={{ fontSize: '12px' }}>
                    <span style={{ fontWeight: 600 }}>Workers AI</span>
                    <span style={{ color: 'var(--text-secondary)' }}> — always available on your account. Agents will use it automatically for LLM inference at the edge.</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => void handleSetup()}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px', background: '#f6821f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                    <Zap size={13} /> Create Resources
                  </button>
                  <button onClick={() => { setWizard('idle'); void fetchAccounts() }}
                    style={{ padding: '10px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
                    Skip
                  </button>
                </div>
              </div>

            ) : wizard === 'connecting' ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Loader2 size={32} color="#f6821f" style={{ animation: 'spin 1s linear infinite', margin: '0 auto 12px', display: 'block' }} />
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Connecting to Cloudflare...</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Fetching your account and resources</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Left: Instructions */}
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Get your Global API Key</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                    {[
                      'Go to Cloudflare → Profile',
                      'Click "API Tokens" tab',
                      'Find "Global API Key" → View',
                      'Copy the key',
                    ].map((step, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'rgba(246,130,31,0.15)', color: '#f6821f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{step}</span>
                      </div>
                    ))}
                  </div>
                  <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#f6821f', color: 'white', borderRadius: '8px', fontSize: '12px', fontWeight: 600, textDecoration: 'none' }}>
                    <ExternalLink size={12} /> Open Cloudflare Profile
                  </a>
                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                    <Lock size={11} /> Credentials encrypted with AES-256
                  </div>
                  <div style={{ marginTop: '8px', padding: '8px 10px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '6px', fontSize: '11px', color: '#b45309' }}>
                    Note: R2 Storage requires a payment method on your Cloudflare account. Add a credit card in CF Dashboard → Billing before creating R2 buckets.
                  </div>
                </div>

                {/* Right: Form */}
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Enter credentials</div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Cloudflare Email</label>
                    <input type="email" placeholder="your@email.com" value={email}
                      onChange={e => setEmail(e.target.value)}
                      style={{ width: '100%', padding: '9px 12px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '5px' }}>Global API Key</label>
                    <input type="password" placeholder="Paste your Global API Key" value={apiKey}
                      onChange={e => setApiKey(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { void handleConnect() } }}
                      style={{ width: '100%', padding: '9px 12px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => void handleConnect()}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '10px', background: '#f6821f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                      <Shield size={13} /> Connect Securely
                    </button>
                    {accounts.length > 0 && (
                      <button onClick={() => setWizard('idle')}
                        style={{ padding: '10px 14px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Connected Accounts */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px', padding: '20px 0' }}>
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {accounts.map(acc => (
            <div key={acc.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '14px', overflow: 'hidden' }}>

              {/* Account header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-color)', background: 'linear-gradient(135deg, rgba(246,130,31,0.04) 0%, transparent 100%)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(246,130,31,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Cloud size={18} color="#f6821f" />
                  </div>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: 700 }}>{acc.account_name || acc.account_id}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{acc.account_id}</div>
                  </div>
                  <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '20px', background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 600 }}>● Active</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {acc.last_synced_at && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Synced {new Date(acc.last_synced_at).toLocaleTimeString()}</span>}
                  <button onClick={() => void handleSync(acc.id)} disabled={syncing === acc.id}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '7px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '12px' }}>
                    <RefreshCw size={11} style={syncing === acc.id ? { animation: 'spin 1s linear infinite' } : {}} /> Sync
                  </button>
                  <button onClick={() => {
                    setConnectedAcc({ id: acc.id, name: acc.account_name })
                    setSetupForm({
                      createD1: acc.d1_count === 0, d1Name: 'runai-db',
                      createR2: acc.r2_count === 0, r2Name: 'runai-storage',
                      createKv: acc.kv_count === 0, kvName: 'runai-cache',
                      createGateway: true, gatewayName: 'runai-gateway',
                    })
                    setWizard('setup')
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', background: 'rgba(246,130,31,0.1)', border: '1px solid rgba(246,130,31,0.3)', borderRadius: '7px', cursor: 'pointer', color: '#f6821f', fontSize: '12px', fontWeight: 600 }}>
                    <Zap size={11} /> Setup
                  </button>
                  <a href={`https://dash.cloudflare.com/${acc.account_id}`} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '7px', color: 'var(--text-secondary)', fontSize: '12px', textDecoration: 'none' }}>
                    <Globe size={11} /> Dashboard
                  </a>
                  <button onClick={() => void handleDisconnect(acc.id)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '6px', display: 'flex', borderRadius: '6px' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Resource cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
                {TABS.map(t => (
                  <ResourceCard key={t.key}
                    icon={t.icon} label={t.label} count={acc[t.countKey] as number}
                    desc={t.desc} color={t.color}
                    active={getTab(acc.id) === t.key}
                    onClick={() => setTab(acc.id, t.key)}
                  />
                ))}
              </div>

              {/* Explorer */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid var(--border-color)' }}>
                  {(() => {
                    const t = TABS.find(x => x.key === getTab(acc.id)) ?? TABS[0]
                    const Icon = t.icon
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Icon size={14} color={t.color} />
                        <span style={{ fontSize: '13px', fontWeight: 600 }}>{t.label}</span>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{acc[t.countKey] as number} resources</span>
                      </div>
                    )
                  })()}
                  <a href={`https://dash.cloudflare.com/${acc.account_id}`} target="_blank" rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-secondary)', textDecoration: 'none' }}>
                    <ExternalLink size={11} /> Open in Cloudflare
                  </a>
                </div>
                <Explorer account={acc} tab={getTab(acc.id)} />
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
